import React from "react";
import { interpolate, useCurrentFrame, useVideoConfig } from "remotion";
import type { Point } from "@veyso/core";
import { easingFor } from "./easing.js";

export interface AnimatedCursorProps {
  /** Normalized 0..1 start point. */
  from: Point;
  /** Normalized 0..1 end point. */
  to: Point;
  /** Frames spent travelling. */
  travelFrames?: number;
  click?: boolean;
  scale?: number;
}

/**
 * A synthetic pointer. Screen recordings often have a hard-to-see system cursor,
 * or none at all when the capture excludes it, so the video draws its own and
 * animates it toward whatever the user acted on.
 */
export const AnimatedCursor: React.FC<AnimatedCursorProps> = ({ from, to, travelFrames, click = false, scale = 1 }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  const travel = travelFrames ?? Math.round(fps * 0.45);

  const progress = interpolate(frame, [0, travel], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easingFor("ease-out"),
  });

  const x = interpolate(progress, [0, 1], [from.x, to.x]) * width;
  const y = interpolate(progress, [0, 1], [from.y, to.y]) * height;

  // The click lands the moment travel finishes: press in, then release.
  const clickFrame = frame - travel;
  const pressed = click && clickFrame >= 0 && clickFrame < fps * 0.18;
  const rippleProgress = click
    ? interpolate(clickFrame, [0, fps * 0.5], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })
    : 0;
  const showRipple = click && clickFrame >= 0 && rippleProgress < 1;

  const size = 34 * scale;

  return (
    <div style={{ position: "absolute", left: x, top: y, pointerEvents: "none" }}>
      {showRipple && (
        <div
          style={{
            position: "absolute",
            left: -size * 1.6 * rippleProgress,
            top: -size * 1.6 * rippleProgress,
            width: size * 3.2 * rippleProgress,
            height: size * 3.2 * rippleProgress,
            borderRadius: "50%",
            border: `${Math.max(1, 4 * (1 - rippleProgress))}px solid rgba(125,211,252,${1 - rippleProgress})`,
          }}
        />
      )}
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        style={{
          display: "block",
          transform: `scale(${pressed ? 0.82 : 1})`,
          filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.5))",
        }}
      >
        <path
          d="M4 2 L4 20 L9 15.5 L12 22 L15 20.5 L12 14.5 L19 14 Z"
          fill="#ffffff"
          stroke="#111827"
          strokeWidth={1.4}
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};
